import { useEffect, useMemo, useState } from 'react';
import { MACHINE_PRESETS, MODELS, findMachine, findModel, findRuntime } from '../catalog.js';
import { allListings, canIRunListing, findListingBySlug, searchListings } from '../directory.js';
import type { DirectoryListing } from '../types.js';
import { VerdictPill } from '../ui.js';

interface DirectoryTabProps {
  mode: 'beginner' | 'pro';
  initialSlug?: string;
  onChangeSlug: (slug: string | undefined) => void;
}

const PAGE_TYPES: Array<{ id: DirectoryListing['pageType'] | 'all'; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'machine', label: 'Machines' },
  { id: 'gpu', label: 'GPUs' },
  { id: 'apple-chip', label: 'Apple Silicon' },
  { id: 'model', label: 'Models' },
  { id: 'runtime', label: 'Runtimes' },
  { id: 'compare', label: 'Compares' },
  { id: 'can-i-run', label: 'Can I run?' },
];

export function DirectoryTab({ mode, initialSlug, onChangeSlug }: DirectoryTabProps) {
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<DirectoryListing['pageType'] | 'all'>('all');
  const [focused, setFocused] = useState<DirectoryListing | null>(
    initialSlug ? (findListingBySlug(initialSlug) ?? null) : null,
  );
  const [pairModel, setPairModel] = useState<string>(MODELS[0].id);
  const [pairMachine, setPairMachine] = useState<string>(MACHINE_PRESETS[0].id);

  useEffect(() => {
    if (initialSlug && (!focused || focused.slug !== initialSlug)) {
      setFocused(findListingBySlug(initialSlug) ?? null);
    }
  }, [initialSlug, focused]);

  const listings = useMemo(() => {
    const base = query ? searchListings(query, 80) : allListings();
    return filter === 'all' ? base : base.filter((l) => l.pageType === filter);
  }, [query, filter]);

  const canIRun = canIRunListing(pairModel, pairMachine);

  function openListing(l: DirectoryListing) {
    setFocused(l);
    onChangeSlug(l.slug);
  }

  function closeListing() {
    setFocused(null);
    onChangeSlug(undefined);
  }

  return (
    <div className="lab-directory">
      <header className="lab-directory-head">
        <div>
          <span className="section-kicker">Public directory</span>
          <h3>Can I Run It Local? · by Vibetime</h3>
          <p>
            Every machine, GPU, Apple chip, model, runtime, comparison, and pair has its own page. Each
            listing is generated from the same fit engine that powers Scan and Routing. Slugs below double as
            deep links — share <code>#/lab/directory/&lt;slug&gt;</code>.
          </p>
        </div>
        <div className="lab-directory-search">
          <input
            type="search"
            placeholder="Search: rtx 4090, qwen coder, m3 max…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="lab-input"
          />
          <div className="lab-directory-filters">
            {PAGE_TYPES.map((t) => (
              <button
                key={t.id}
                type="button"
                className={`lab-filter-chip ${filter === t.id ? 'lab-filter-active' : ''}`}
                onClick={() => setFilter(t.id)}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      <section className="lab-directory-pair">
        <h4>Can I run this on that?</h4>
        <div className="lab-pair-row">
          <label>
            <span>Model</span>
            <select className="lab-select" value={pairModel} onChange={(e) => setPairModel(e.target.value)}>
              {MODELS.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.displayName}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>on Machine</span>
            <select
              className="lab-select"
              value={pairMachine}
              onChange={(e) => setPairMachine(e.target.value)}
            >
              {MACHINE_PRESETS.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.nickname}
                </option>
              ))}
            </select>
          </label>
          <button type="button" className="ghost-button" onClick={() => openListing(canIRun)}>
            Open page
          </button>
        </div>
        <article className="lab-pair-verdict">
          <VerdictPill verdict={canIRun.topModels[0]?.verdict ?? 'good'} />
          <div>
            <strong>{canIRun.title}</strong>
            <p>{canIRun.subtitle}</p>
            <code>/{canIRun.slug}</code>
          </div>
        </article>
      </section>

      <p className="lab-directory-count">{listings.length} pages</p>

      <div className="lab-directory-grid">
        {listings.map((l) => (
          <button key={l.slug} type="button" className="lab-directory-card" onClick={() => openListing(l)}>
            <span className="lab-directory-type">{l.pageType}</span>
            <h4>{l.title}</h4>
            <p>{l.subtitle}</p>
            <div className="lab-directory-meta">
              <span>{l.aggregate.avgTps} tok/s avg</span>
              <span>{l.topModels.length} top picks</span>
            </div>
            <code>/{l.slug}</code>
          </button>
        ))}
        {listings.length === 0 && <p className="lab-empty">No matches — try another query.</p>}
      </div>

      {focused && <DirectoryFocused listing={focused} onClose={closeListing} />}

      {mode === 'beginner' && (
        <aside className="lab-directory-note">
          These pages ship as the seed for a public browsable directory — structure is the same whether
          rendered inside the dashboard or as standalone HTML. Each slug becomes a route.
        </aside>
      )}
    </div>
  );
}

function DirectoryFocused({ listing, onClose }: { listing: DirectoryListing; onClose: () => void }) {
  const subjectLabel = (id: string): string => {
    const m = findModel(id);
    if (m) return m.displayName;
    const machine = findMachine(id);
    if (machine) return machine.nickname;
    return id;
  };
  const runtime = listing.pageType === 'runtime' ? findRuntime(listing.primary.id) : undefined;

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <dialog className="lab-directory-focused" open onClose={onClose}>
      <button
        type="button"
        className="lab-directory-backdrop"
        onClick={onClose}
        aria-label="Close directory page"
      />
      <div
        className="lab-directory-focused-panel panel"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        aria-label={listing.title}
      >
        <header>
          <div>
            <span className="section-kicker">{listing.pageType}</span>
            <h3>{listing.title}</h3>
            <p>{listing.subtitle}</p>
            <code>/{listing.slug}</code>
          </div>
          <button type="button" className="ghost-button" onClick={onClose}>
            Close
          </button>
        </header>

        {runtime && (
          <section className="lab-runtime-detail">
            <h4>Strengths</h4>
            <ul>
              {runtime.strengths.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ul>
            <h4>Accelerators</h4>
            <p>{runtime.accelerators.join(' · ')}</p>
            <h4>Beginner friendly?</h4>
            <p>{runtime.beginnerFriendly ? 'Yes — safe default.' : 'No — power-user setup.'}</p>
          </section>
        )}

        <section>
          <h4>Top picks</h4>
          {listing.topModels.length === 0 ? (
            <p className="lab-empty">No picks for this listing.</p>
          ) : (
            <ul className="lab-listing-picks">
              {listing.topModels.map((t) => (
                <li key={`${t.modelId}-${t.quantization}`}>
                  <strong>{subjectLabel(t.modelId)}</strong>
                  <VerdictPill verdict={t.verdict} />
                  <span>{t.quantization}</span>
                  <span>score {t.score}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="lab-listing-meta">
          <h4>Aggregate</h4>
          <dl>
            <div>
              <dt>Bench runs</dt>
              <dd>{listing.aggregate.benchRuns}</dd>
            </div>
            <div>
              <dt>Avg tok/s</dt>
              <dd>{listing.aggregate.avgTps}</dd>
            </div>
            <div>
              <dt>Avg quality</dt>
              <dd>{listing.aggregate.avgQuality}</dd>
            </div>
            <div>
              <dt>Updated</dt>
              <dd>{new Date(listing.updatedAt).toLocaleString()}</dd>
            </div>
          </dl>
        </section>
      </div>
    </dialog>
  );
}
